import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './Block.styled';
import { convertColorToThemeHexCode } from '../../styles/colors.util';
import CTAPropTypes from '../../prop-types/cta';
import CTABlock from '../CTABlock/CTABlock';
import { CustomPropType } from './Block.utils';
import { ContainerQueryContext } from '../../util/ContainerQuery.context';

/**
 * `Block` displays pre-styled HTML in between an optional icon and button(s).
 */
const Block = ({
  alignment,
  backgroundColor,
  ctas,
  icon,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  className,
  children
}) => {
  const { styleQueryMode } = useContext(ContainerQueryContext);

  return (
    <Styled.Block
      $backgroundColor={backgroundColor}
      paddingTop={paddingTop}
      paddingRight={paddingRight}
      paddingBottom={paddingBottom}
      paddingLeft={paddingLeft}
      className={className}
      $styleQueryMode={styleQueryMode}
    >
      <Styled.Editorial $alignment={alignment} $styleQueryMode={styleQueryMode}>
        {icon?.url && (
          <Styled.Icon
            $alignment={alignment}
            src={icon.url}
            alt="icon"
            $styleQueryMode={styleQueryMode}
          />
        )}
        {children && (
          <Styled.RichText
            $backgroundColor={backgroundColor}
            $styleQueryMode={styleQueryMode}
          >
            {children}
          </Styled.RichText>
        )}
        {!!ctas?.length && <CTABlock ctas={ctas} color={backgroundColor} />}
      </Styled.Editorial>
    </Styled.Block>
  );
};

Block.propTypes = {
  /**
   * Left align or center variation
   */
  alignment: PropTypes.oneOf(['left', 'center']),
  /**
   * A hex value to use as the background color of `Block`
   */
  backgroundColor: PropTypes.string,
  /**
   * Content to display in block. Only raw HTML accepted.
   */
  children: CustomPropType.anyOfDeepTags(
    ['h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'p', 'li', 'a', 'b', 'i'],
    '__skipValidate'
  ),
  /**
   * For internal use only.
   * Used to bypass `children` validation due to complexities with validation.
   */
  __skipValidate: PropTypes.bool, // eslint-disable-line
  /**
   * CSS Classname
   */
  className: PropTypes.string,
  /**
   * A list of links
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CTAPropTypes)),
  /**
   * An icon object
   */
  icon: PropTypes.shape({
    /**
     * An image URL
     */
    url: PropTypes.string.isRequired
  }),
  /**
   * overrides top padding if necessary
   * if not passed, uses default padding set in the styles
   */
  paddingTop: PropTypes.string,
  /**
   * overrides right padding if necessary
   * if not passed, uses default padding set in the styles
   */
  paddingRight: PropTypes.string,
  /**
   * overrides bottom padding if necessary
   * if not passed, uses default padding set in the styles
   */
  paddingBottom: PropTypes.string,
  /**
   * overrides left padding if necessary
   * if not passed, uses default padding set in the styles
   */
  paddingLeft: PropTypes.string
};

Block.defaultProps = {
  alignment: 'center',
  backgroundColor: convertColorToThemeHexCode('white'),
  children: null,
  className: '',
  ctas: [],
  icon: null,
  paddingTop: '',
  paddingRight: '',
  paddingBottom: '',
  paddingLeft: ''
};

export default Block;
